import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Table, Button, Form, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';

const PermissionsAdmin = () => {
  const [rolesWithPermissions, setRolesWithPermissions] = useState([]);
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    role_id: '',
    user_id: '',
    permission_name: '',
    value: '0',
  });
  const [selectedPermission, setSelectedPermission] = useState(null);

  useEffect(() => {
    fetchRolesWithPermissions();
    fetchUsers();
  }, []);

  const fetchRolesWithPermissions = async () => {
    try {
      const response = await axios.get('https://api.biznetusa.com/api/get-roles');
      setRolesWithPermissions(response.data.roles || []);
    } catch (error) {
      console.error('Error fetching roles and permissions', error);
      toast.error('Failed to fetch roles and permissions');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch('https://api.biznetusa.com/api/all-users');
      const data = await response.json();
      if (response.ok && data.allusers) {
        setUsers(data.allusers);
      } else {
        toast.error('Failed to fetch users');
      }
    } catch (error) {
      toast.error('An error occurred while fetching users');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddPermission = async () => {
    try {
      await axios.post('https://api.biznetusa.com/api/store-permissions', formData);
      fetchRolesWithPermissions();
      setShowModal(false);
      toast.success('Permission added successfully');
    } catch (error) {
      console.error('Error adding permission', error);
      toast.error('Failed to add permission');
    }
  };

  const handleUpdatePermission = async () => {
    try {
      await axios.put(`https://api.biznetusa.com/api/update-permissions/${selectedPermission.id}`, formData);
      fetchRolesWithPermissions();
      setShowModal(false);
      toast.success('Permission updated successfully');
    } catch (error) {
      console.error('Error updating permission', error);
      toast.error('Failed to update permission');
    }
  };

  const handleDeletePermission = async (id) => {
    try {
      await axios.delete(`https://api.biznetusa.com/api/deletepermissions/${id}`);
      fetchRolesWithPermissions();
      toast.success('Permission deleted successfully');
    } catch (error) {
      console.error('Error deleting permission', error);
      toast.error('Failed to delete permission');
    }
  };

  const handleOpenModal = (permission = null, role_id = '') => {
    if (permission) {
      setSelectedPermission(permission);
      setFormData({
        role_id: role_id,
        user_id: permission.user_id || '',
        permission_name: permission.permission_name,
        value: permission.value,
      });
    } else {
      setFormData({
        role_id: '',
        user_id: '',
        permission_name: '',
        value: '0',
      });
      setSelectedPermission(null);
    }
    setShowModal(true);
  };

  return (
    <div className='px-2 py-3'>
      <h2>Manage Permissions</h2>
      <Button onClick={() => handleOpenModal()}>Add Permission</Button>
      <Table striped bordered hover>
        <thead>
          <tr>
            <th>ID</th>
            <th>Role Name</th>
            <th>Permission Name</th>
            <th>Value</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rolesWithPermissions.map((role) => (
            role.permissions.map((permission) => (
              <tr key={permission.id}>
                <td>{permission.id}</td>
                <td>{role.role_name}</td>
                <td>{permission.permission_name}</td>
                <td>{permission.value}</td>
                <td>
                  <Button
                    variant="warning"
                    onClick={() => handleOpenModal(permission, role.id)}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => handleDeletePermission(permission.id)}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))
          ))}
        </tbody>
      </Table>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedPermission ? 'Edit Permission' : 'Add Permission'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group controlId="formRoleId">
              <Form.Label>Select Role</Form.Label>
              <Form.Control
                as="select"
                name="role_id"
                value={formData.role_id}
                onChange={handleInputChange}
              >
                <option value="">Select Role</option>
                {rolesWithPermissions.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.role_name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            <Form.Group controlId="formUserId">
              <Form.Label>Select User</Form.Label>
              <Form.Control
                as="select"
                name="user_id"
                value={formData.user_id}
                onChange={handleInputChange}
              >
                <option value="">Select User</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            <Form.Group controlId="formPermissionName">
              <Form.Label>Permission Name</Form.Label>
              <Form.Control
                type="text"
                name="permission_name"
                value={formData.permission_name}
                onChange={handleInputChange}
              />
            </Form.Group>
            <Form.Group controlId="formValue">
              <Form.Label>Value</Form.Label>
              <Form.Control
                type="text"
                name="value"
                value={formData.value}
                onChange={handleInputChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={selectedPermission ? handleUpdatePermission : handleAddPermission}>
            {selectedPermission ? 'Update' : 'Add'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PermissionsAdmin;
